<template>
    <div class="overflow-x-hidden">
        <section
            id="how-it-works"
            class="timeline-section text-center  grid flex flex-wrap justify-content-center align-items-center py-4 px-4 md:px-6 relative">
            <div v-if="bgUrl" class="background-image" :style="{ backgroundImage: `url('${bgUrl}')`, zIndex: 0 }">
            </div>
            <div class="col-10 relative">
                <span class="badge mb-3">Simple & Effective</span>
                <h2 class="mb-2 LandingPage-Title">
                    How it works
                </h2>
                <p class="Paragraph-Regular font-semibold ">
                    Get started with ILearn’d in just four simple steps. Our streamlined process makes finding and
                    learning with expert tutors effortless and enjoyable.
                </p>
            </div>
            <div class="container px-4 md:px-6 relative py-2 md:py-6">
                <div class="card">

                    <Timeline v-if="!isMobile" :value="steps" align="alternate" class="customized-timeline"  
                       >
                        <template #marker="slotProps">
                            <div class="timeline-connector">
                                <span
                                    class="grid flex flex-column align-items-center justify-content-center rounded-full timeline-icon shadow-lg">
                                    <i :class="slotProps.item.icon" class="text-sm"></i>
                                </span>
                            </div>

                        </template>
                        <template #opposite="slotProps">
                            <div :class="slotProps.item.index % 2 === 0 ? 'order-1' : 'order-2'"
                                class="grid flex col-12 md:col-6 align-items-start justify-content-center py-4 w-full md:px-8" v-animateonscroll="{ enterClass: 'fade-in-up', leaveClass: 'fade-out' }">
                                <div class="step-content">
                                    <p class="badge surface-card mb-0 border-1">
                                        step 0{{ slotProps.item.index + 1 }}
                                    </p>
                                    <h3 class="H5-SemiBold  mb-3">{{ slotProps.item.title }}</h3>
                                    <p class=" Paragraph-Medium subtitle mb-0">
                                        {{ slotProps.item.subtitle }}
                                    </p>
                                    <p class=" Label leading-relaxed mb-0">
                                        {{ slotProps.item.description }}
                                    </p>
                                    <!-- Pills Section -->
                                    <div class="pills-container pt-4 "
                                        :class="slotProps.item.index % 2 === 0 ? 'md:pl-8' : 'md:pr-8'">
                                        <Chip v-for="(pill, pillIndex) in slotProps.item.pills" :key="pillIndex"
                                            :label="pill" class="custom-chip Label" icon="pi pi-check-circle" />
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template #content="slotProps">
                            <div :class="slotProps.item.index % 2 === 0 ? 'order-2' : 'order-1'"
                                class="grid flex col-12 lg:col-6  align-items-start justify-content-center py-4 w-full md:px-8" v-animateonscroll="{ enterClass: 'fade-in-up'}">
                                <div class="step-visual w-full relative">
                                    <img :src="slotProps.item.imageUrl" :alt="slotProps.item.title"
                                        class="w-full border-round-xl shadow-2" />
                                    <div
                                        class="gradient-overlay absolute bottom-0 left-0 w-full h-full pointer-events-none border-round-xl">
                                    </div>
                                    <div class="image-overlay absolute bottom-0 w-full p-4 text-white ">
                                        <h3 class="H6-Medium text-white mb-0">{{ slotProps.item.title }}</h3>
                                        <p class="Caption mb-0 mt-1 text-white opacity-90">{{ slotProps.item.subtitle }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </template>

                    </Timeline>
                    <div v-else class="flex flex-column gap-5">
                        <div v-for="(step, index) in steps" :key="index"
                            class="mobile-step-card p-5 border-round-2xl surface-card shadow-2">

                            <div class="flex align-items-center gap-3 mb-4">
                                <div class="step-badge flex align-items-center justify-content-center border-round-lg">
                                    <span class="step-number text-sm font-semibold">0{{ index + 1 }}</span>
                                </div>
                                <h3 class="H5-SemiBold mb-0 flex-1">{{ step.title }}</h3>
                            </div>

                            <p class="Paragraph-Medium mb-3 ">{{ step.subtitle }}</p>

                            <div class="step-image-container mb-4">
                                <img :src="step.imageUrl" :alt="step.title" class="w-full border-round-xl shadow-1" />
                            </div>

                            <p class="Label leading-relaxed mb-4">{{ step.description }}</p>

                            <!-- Pills -->
                            <div class="flex flex-wrap gap-2">
                                <Chip v-for="(pill, pillIndex) in step.pills" :key="pillIndex" :label="pill"
                                    class="custom-chip Label " icon="pi pi-check-circle" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
<script setup>
import Timeline from 'primevue/timeline';
import Chip from 'primevue/chip';
import { usePublicUrl } from "@/utils/getPublicUrl"
import { ref, onMounted, onUnmounted } from 'vue';

const bgUrl = usePublicUrl("public/how-it-works.jpg");

const steps = ref([
    {
        index: 0,
        title: 'Search & Discover',
        description: 'Use our intelligent search filters to browse through hundreds of verified tutors. Filter by subject, grade level, availability, and even teaching style to find exactly what you need.',
        icon: 'pi pi-search',
        image: 'public/image1.jpg',
        subtitle: 'Find Your Perfect Match',
        imageUrl: "",
        pills: [
            "Smart search filters",
            "Location-based results",
            "Price comparison",
            "Tutor availability"
        ]
    },
    {
        index: 1,
        title: 'Explore Profiles',
        description: 'Dive deep into tutor profiles with detailed qualifications, experience and student reviews . See their teaching methods and success stories',
        icon: 'pi pi-user',
        image: 'public/image2.png',
        subtitle: 'Get to Know Your Tutor/Institute',
        imageUrl: "",
        pills: [
            "Verified qualifications",
            "Student reviews",
            "Teaching samples",
        ]
    },
    {
        index: 2,
        title: 'Book & Schedule',
        description: 'Found the perfect tutor? Great! Book your session in just a few clicks, choose the time that works best for you, and get ready to learn.',
        icon: 'pi pi-calendar',
        image: 'public/image4.png',
        subtitle: 'Flexible Booking Made Easy',
        imageUrl: "",
        pills: [
            "Instant booking",
            "Flexible scheduling",
            "Online",
            "Calendar integration"
        ]
    },
    {
        index: 3,
        title: 'Learn & Grow',
        description: 'Join your sessions and start learning with personalised attention. Track your progress, receive feedback, and watch your grades improve with expert guidance.',
        icon: 'pi pi-book',
        image: 'public/image3.jpg',
        subtitle: 'Start Your Learning Journey',
        imageUrl: "",
        pills: [
            "Personalized lessons",
            "Progress tracking",
            "Regular feedback",
            "Performance analytics"
        ]
    }
]);
onMounted(async () => {
    for (let feature of steps.value) {
        try {
            const url = await usePublicUrl(feature.image);
            feature.imageUrl = url;
        } catch (error) {
            console.error('Error loading image:', feature.image, error);
        }
    }
});
const isMobile = ref(window.innerWidth < 768);

const handleResize = () => {
    isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

</script>
<style scoped>
.LandingPage-Title {
    color: #1D97BD;
}

.subtitle {
    color: #7792D0;
}

.background-image {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 35%;
}

.timeline-connector {
    padding-top: 20vh;
}

.timeline-icon {
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #7792D0;
    border-radius: 50% !important;
    color: #7792D0;
    background-color: white;
}

:deep(.customized-timeline .p-timeline-event-opposite) {
    flex: 1;
    padding: 0 1rem;
}

:deep(.customized-timeline .p-timeline-event-content) {
    flex: 1;
    padding: 0 1rem;
}

:deep(.customized-timeline .p-timeline-event-connector) {
    background-color: #7792D0;
}

:deep(.p-timeline-event-separator) {
    flex: 0;
    display: flex;
    align-items: center;
    flex-direction: column;
    width: 2px;
    background-color: #7792D0 !important;
}

.custom-chip {
    background-color: transparent;
}

:deep(.custom-chip .p-chip-icon) {
    color: #7792D0 !important;
}

.gradient-overlay {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 64.19%, rgba(0, 0, 0, 0.87) 96.81%);
}
</style>